import { useEffect, useState } from "react";
import { getPageTemplateOverride } from "../auth/apiPageTemplateOverride";
import { resolveTemplateJsonDoc } from "../pageInside/PageBuilder/ContentPageTemplates";

const EMPTY_RESULT = { doc: null, htmlSource: null, htmlSourcePages: null, loading: false };

// ONE central resolver hook for every real Content Page render site
// (Layout/Footer.js's AboutModal, howToStep.jsx's onboarding funnel) -- task
// spec §22/§9: "Ne copie pas ce `if` dans 50 composants. Je veux un resolver
// central." `pageKey` is one of contentPageUtils.js's own real
// CONTENT_PAGE_KEYS (aboutUs, becomeRoyalty, ...) -- this hook turns it into
// the real templateKey (`content.<pageKey>`) itself, so callers never
// construct that string by hand.
//
// ARCHITECTURE CORRECTION: a Content Page's DATA SOURCE is fixed and NEVER
// selectable/redirectable -- About Us always renders with About Us's own
// data, full stop (a prior mission briefly added a `dataSourceKey` redirect;
// it was explicitly retracted). What CAN vary is the DESIGN: the backend's
// `getOverride` endpoint, for a `content.*` templateKey, already resolves
// this through the layered "selected Page Template, else this page's own
// legacy override" logic server-side (resolveContentPageTemplate) -- this
// hook only ever needs to unwrap whatever HTML that resolution settled on;
// it never touches or redirects which key's data the caller builds.
//
// REAL BUG FIXED HERE (task spec's own §47 "wrong object shape" prediction):
// GET /page-template-overrides/:templateKey resolves `pagebuilderDoc` to the
// RAW `pagebuilders` Mongo record -- { _id, title, owner, doc: { pages: [...] },
// htmlSource, htmlSourcePages, ... }. The actual JSON tree PageRenderer's own
// `doc` prop expects lives ONE LEVEL DEEPER, at `pagebuilderDoc.doc`. Handing
// the outer record straight to `<PageRenderer doc={...} />` gives it a `doc`
// whose `.pages` is undefined -- which is exactly PageRenderer.jsx's own
// "No pages to display." fallback condition
// (`if (!doc || !doc.pages || !doc.pages.length)`). This hook unwraps that
// ONE time, centrally, so no caller has to know/repeat the real record shape.
// It also forwards `htmlSource`/`htmlSourcePages` (the Edit HTML Code
// override, a SEPARATE mechanism from the JSON block tree -- see
// pageBuilderModel.js) so a Content Page override built with either system
// renders correctly; PageRenderer itself already knows how to pick between
// them (see its own `activePageHtmlSource` resolution).
//
// `doc`/`htmlSource`/`htmlSourcePages` all stay null whenever the resolved
// template (selected or legacy) has NO custom HTML of its own -- that is a
// perfectly valid state (Active+Priority ON, original source template in
// effect), and the caller's own existing rendering (built from ITS OWN,
// unredirected data) is exactly what must run in that case, same as when
// there is no override at all.
//
// Fails safe (task spec §23/§26): ANY failure (network error, 404, malformed
// key, priority OFF, no override at all) resolves everything to null --
// the caller's OWN existing rendering (the real CONTENT_PAGE_KEYS text
// sections, unchanged) is what runs whenever `doc` comes back null, so a
// broken/unreachable override service can only ever fall back to the
// original page, never break it.
//
// Pass `null`/`undefined`/`""` for pageKey to skip fetching entirely (e.g.
// while a modal containing the content page isn't even open yet) -- task
// spec §42/§23: "ne fais pas un appel API chaque fois..."
export function useContentPageOverride(pageKey) {
  const [state, setState] = useState(EMPTY_RESULT);

  useEffect(() => {
    if (!pageKey) {
      setState(EMPTY_RESULT);
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    getPageTemplateOverride(`content.${pageKey}`)
      .then((result) => {
        if (cancelled) return;
        const inEffect = Boolean(result?.priority);
        const record = inEffect ? result.pagebuilderDoc : null;
        // `record.doc` is the JSON tree PageRenderer expects; a record saved
        // through Edit HTML Code alone can still have a minimal `doc.pages`
        // (the Builder always starts a page with at least one entry) plus a
        // real htmlSource/htmlSourcePages override -- both are forwarded so
        // PageRenderer can pick the right one, exactly as it already does
        // for every other consumer (ShopUser.js, PageBuilder.jsx's own
        // Preview panel).
        // SYSTEM/DEFAULT TEMPLATE selected with NO custom HTML of its own yet
        // (fix-400 + system-template-selection mission §5/§16/§21): its real
        // SOURCE design lives in frontend code (`tpl.make()` --
        // ContentPageTemplates.js's own CONTENT_TEMPLATES), never a database
        // row (see the backend's own CODE_FALLBACK_GROUPS comment).
        // `resolveTemplateJsonDoc` is the ONE shared resolver for this (Classic
        // Content preload mission §7/§30: PageBuilder.jsx's own "Edit HTML
        // Code" starter-HTML resolution reuses this SAME function, so the two
        // can never drift into different fallback rules).
        const doc = inEffect ? resolveTemplateJsonDoc({ pagebuilderDoc: record, templateKey: result?.templateKey, group: result?.group }) : null;
        setState({
          doc,
          htmlSource: record?.htmlSource || null,
          htmlSourcePages: record?.htmlSourcePages || null,
          loading: false,
        });
      })
      .catch(() => {
        if (!cancelled) setState(EMPTY_RESULT);
      });
    return () => {
      cancelled = true;
    };
  }, [pageKey]);

  return state;
}
